import React from 'react';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';

import { flowComponentRegistry } from './componentRegistry';
import type { FlowActionEvent, FlowComponentConfig, FlowScreenConfig, FlowValues } from './types';

interface ScreenRendererProps {
  excludeComponentIds?: ReadonlySet<string>;
  goBack: (fallbackScreenId?: string) => void;
  goNext: () => void;
  goToScreen: (screenId: string) => void;
  isLastScreen: boolean;
  onAction?: (action: FlowActionEvent) => void;
  persistentContext?: boolean;
  screen: FlowScreenConfig;
  setValue: (componentId: string, value: unknown) => void;
  submitValueAndGoNext: (componentId: string, value: unknown) => void;
  values: FlowValues;
}

const ScreenRenderer: React.FC<ScreenRendererProps> = ({
  excludeComponentIds,
  goBack,
  goNext,
  goToScreen,
  isLastScreen,
  onAction,
  persistentContext = false,
  screen,
  setValue,
  submitValueAndGoNext,
  values,
}) => {
  const componentGap = (
    type: FlowComponentConfig['type'],
    previousType?: FlowComponentConfig['type'],
  ): number => {
    if (!previousType || type === 'topBar') return 0;
    if (previousType === 'topBar') return 1;
    if (type === 'subheader') return 0;
    return 2;
  };

  const renderComponents = (
    components: FlowComponentConfig[],
    gapForComponent = componentGap,
  ) =>
    components
      .filter((component) => !excludeComponentIds?.has(component.id))
      .map((componentConfig, index, visibleComponents) => {
      const Component = flowComponentRegistry[componentConfig.type] as React.FC<any> | undefined;
      const previousType = visibleComponents[index - 1]?.type;

      if (!Component) {
        return (
          <Alert key={componentConfig.id} severity="warning">
            Unsupported component: {(componentConfig as FlowComponentConfig).type}
          </Alert>
        );
      }

      return (
        <Box
          data-flow-component={componentConfig.type}
          key={componentConfig.id}
          sx={{
            mt: gapForComponent(componentConfig.type, previousType),
            ...(componentConfig.type === 'topBar'
              ? { mx: -2, width: 'calc(100% + 32px)' }
              : null),
            ...((persistentContext || hasProofMedia) && ['h1', 'subheader'].includes(componentConfig.type)
              ? {
                  '[data-cx-color-scheme="dark"] &': { display: 'none' },
                }
              : null),
          }}
        >
          <Component
            config={componentConfig}
            goBack={goBack}
            goNext={goNext}
            goToScreen={goToScreen}
            isLastScreen={isLastScreen}
            onAction={onAction}
            setValue={setValue}
            submitValueAndGoNext={submitValueAndGoNext}
            value={values[componentConfig.id]}
            values={values}
          />
        </Box>
      );
      });

  const confirmationCard = screen.presentation === 'confirmation_card';
  const recoveryReview = screen.presentation === 'recovery_review';
  const hasProofMedia = screen.components.some((component) => component.type === 'proofOfServiceMedia');
  const topBar = screen.components.filter((component) => component.type === 'topBar');
  const content = screen.components.filter((component) => component.type !== 'topBar');
  const confirmationGap = (
    type: FlowComponentConfig['type'],
    previousType?: FlowComponentConfig['type'],
  ): number => {
    if (!previousType) return 0;
    if (type === 'h1' && previousType === 'visualAsset') return 0.75;
    if (type === 'subheader') return 0.5;
    if (type === 'actionButton' && previousType === 'actionButton') return 1.25;
    if (type === 'actionButton') return 2;
    return 2;
  };

  return (
    <Stack
      sx={{
        flexGrow: persistentContext ? 0 : 1,
        minHeight: 0,
        pb: persistentContext ? 0 : 5,
        px: 2,
        width: '100%',
      }}
    >
      {confirmationCard ? (
        <>
          {renderComponents(topBar)}
          <Card
            data-testid="confirmation-card"
            sx={{
              bgcolor: 'var(--cx-brand-surface)',
              border: '1px solid var(--cx-brand-border)',
              borderRadius: 3,
              boxShadow: 'none',
              color: 'var(--cx-brand-text)',
              mt: 1,
              px: 1.5,
              py: 2,
              '[data-cx-color-scheme="dark"] &': {
                background:
                  'linear-gradient(145deg, color-mix(in srgb, var(--cx-brand-surface) 92%, var(--cx-brand-primary)), var(--cx-brand-surface))',
                borderColor: 'var(--cx-brand-border)',
              },
            }}
          >
            {renderComponents(content, confirmationGap)}
          </Card>
        </>
      ) : recoveryReview ? (
        <>
          {renderComponents(topBar)}
          {renderComponents(content.filter((component) => ['backButton', 'h1'].includes(component.type)))}
          <Card
            sx={{
              bgcolor: 'var(--cx-brand-bg)',
              border: '1px solid var(--cx-brand-border)',
              borderRadius: 1.5,
              boxShadow: 'none',
              mt: 2,
              p: 1.5,
            }}
          >
            {renderComponents(
              content.filter((component) =>
                ['visualAsset', 'h2', 'subheader', 'actionButton'].includes(component.type)
              )
            )}
          </Card>
          {renderComponents(content.filter((component) => component.type === 'reviewSelection'))}
        </>
      ) : (
        <>
          {renderComponents(topBar)}
          {renderComponents(content)}
        </>
      )}
    </Stack>
  );
};

export default ScreenRenderer;
